import * as React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    ACCESS_KEYS_PERMISSION,
    ENTITY_PERMISSION,
    HOME_SCREEN_PERMISSION,
    MEMBERSHIP_PERMISSION,
    MONITORING_PERMISSION,
    PAYMENTS_PERMISSION,
    PERMISSION,
    PRICING_PLANS_PERMISSION,
    SITE_PERMISSION,
    STATISTICS_PERMISSION,
    SYSTEM_PERMISSION,
    TROUBLESHOOTING_PERMISSION,
    USER_PERMISSION,
    VISITOR_PERMISSION,
    WHITE_LISTS_PERMISSION,
    VALIDATIONS_PERMISSION,
} from 'src/features/auth/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useGetRole } from 'src/features/roles/api/useGetRole';
import { LIST_ROLES_RESOURCE } from 'src/features/roles/api/useListRoles';
import { useUpdateRole } from 'src/features/roles/api/useUpdateRole';
import { rolesMessages } from 'src/features/roles/utils/messages';
import PermissionBlock from 'src/features/roles/components/PermissionBlock';
import { GET_PERMISSIONS_RESOURCE } from 'src/features/auth/api/useGetPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import BackButton from 'src/features/navigation/components/BackButton';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { Separator } from 'src/registry/ui/Separator';

function RoleDashboard() {
    const { formatMessage } = useIntl();
    const { roleId = '' } = useParams();
    const { parkingId } = useParking();

    const { data } = useGetRole({ roleId, parkingId, config: { enabled: !!roleId } });
    const { mutate } = useUpdateRole({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ROLES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_PERMISSIONS_RESOURCE] });

            setDisableSaveButton(true);

            toast({
                title: formatMessage(rolesMessages.rolePermissionsChanged),
                description: formatMessage(rolesMessages.rolePermissionsChangesSuccess, {
                    entry: success.name,
                }),
            });
        },
    });
    const [permissions, setPermissions] = React.useState<PERMISSION[]>([]);
    const [disableSaveButton, setDisableSaveButton] = React.useState<boolean>(true);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        if (!data) return;
        setPermissions(data.permissions);
        setTitle(data.name);
        return removeTitle;
    }, [removeTitle, setTitle, data]);

    React.useEffect(() => {
        if (!data) return;
        const sa = [...permissions].sort();
        const sb = [...data.permissions].sort();
        const isEqual = sa.length === sb.length && sa.every((v, i) => v === sb[i]);

        setDisableSaveButton(isEqual);
    }, [data, permissions]);

    const onSubmit = React.useCallback(() => {
        if (!data) return;
        mutate({ roleId, parkingId, data: { name: data.name, permissions } });
    }, [data, mutate, permissions, roleId, parkingId]);

    const { areAllPermissionsChecked, allPermissions } = React.useMemo(() => {
        const allPermissions = Object.values(PERMISSION);
        const areAllPermissionsChecked = allPermissions.length === permissions.length;

        return {
            allPermissions,
            areAllPermissionsChecked,
        };
    }, [permissions.length]);

    return (
        <>
            <BackButton />
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
                <div className="flex w-full flex-row items-center justify-start gap-2">
                    <Checkbox
                        id="all"
                        checked={areAllPermissionsChecked}
                        onCheckedChange={() => setPermissions(areAllPermissionsChecked ? [] : allPermissions)}
                    />
                    <label htmlFor="all">
                        {formatMessage(areAllPermissionsChecked ? rolesMessages.unSelectAll : rolesMessages.selectAll)}
                    </label>
                </div>
            </div>
            <Separator />
            <PermissionBlock
                title={formatMessage(rolesMessages.homeStats)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(HOME_SCREEN_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.entities)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(ENTITY_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.sites)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(SITE_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.users)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(USER_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.system)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(SYSTEM_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.memberships)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(MEMBERSHIP_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.visitors)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(VISITOR_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.payments)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(PAYMENTS_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.pricingPlans)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(PRICING_PLANS_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.monitoring)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(MONITORING_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.statistics)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(STATISTICS_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.validations)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(VALIDATIONS_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.accessKeys)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(ACCESS_KEYS_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.troubleshooting)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(TROUBLESHOOTING_PERMISSION)}
            />
            <PermissionBlock
                title={formatMessage(rolesMessages.whiteLists)}
                rolePermissions={permissions}
                setRolePermissions={setPermissions}
                permissionValues={Object.values(WHITE_LISTS_PERMISSION)}
            />
            <div className="flex w-full justify-end">
                <Button onClick={onSubmit} disabled={disableSaveButton}>
                    <FormattedMessage
                        defaultMessage={rolesMessages.saveChanges.defaultMessage}
                        id={rolesMessages.saveChanges.id}
                    />
                </Button>
            </div>
        </>
    );
}

export default RoleDashboard;
